import useDraftUpdatePipeline from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftUpdatePipeline';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Form,
  Modal,
  ModalProps,
  RawInput,
} from '@payaca/ambrosia';
import { MAX_PIPELINE_TITLE_LENGTH } from '@payaca/constants/pipeline';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

const renamePipelineSchema = z.object({
  title: z
    .string()
    .min(1, 'Pipeline name is required')
    .max(
      MAX_PIPELINE_TITLE_LENGTH,
      `Pipeline name must be ${MAX_PIPELINE_TITLE_LENGTH} characters or fewer`
    ),
});

type RenamePipelineForm = z.infer<typeof renamePipelineSchema>;

type Props = Pick<ModalProps, 'isOpen' | 'onClose'> & {
  pipelineId: string;
  currentTitle?: string;
};

const RenamePipelineModalContent: FC<{
  title?: string;
  onSubmit: (data: RenamePipelineForm) => void;
  onClose?: () => void;
}> = (props) => {
  const { title, onSubmit, onClose } = props;

  const formMethods = useForm<RenamePipelineForm>({
    resolver: zodResolver(renamePipelineSchema),
    values: { title: title ?? '' },
  });

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        <Field name="title">
          <Field.Label>Pipeline name</Field.Label>
          <RawInput
            {...formMethods.register('title')}
            autoFocus
            maxLength={MAX_PIPELINE_TITLE_LENGTH}
          />
        </Field>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" onClick={() => onClose?.()}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

const RenamePipelineModal: FC<Props> = (props) => {
  const { isOpen, onClose, pipelineId, currentTitle } = props;
  const { mutate } = useDraftUpdatePipeline({ pipelineId });

  const onSubmit = (data: RenamePipelineForm) => {
    mutate({ id: pipelineId, title: data.title });
    onClose?.();
  };

  return (
    <Modal title="Rename pipeline" isOpen={isOpen} onClose={onClose}>
      <RenamePipelineModalContent
        title={currentTitle}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Modal>
  );
};

export default RenamePipelineModal;
